import { Survey } from "src/pageDefinitions/types";
import { trackEvent } from "src/utils/api/tracker";
import { resetCurrentAnswers } from "src/utils/redux/slices/currentAnswers";
import {
  saveMainSurveyAnswers,
  saveSurveyAnswers,
  UserDataState,
} from "src/utils/redux/slices/userData";
import { isHM, isUS } from "src/utils/userSegment";
import invariant from "tiny-invariant";
import { ActionHandlerParams } from "..";
import { getPageSetSurvey } from "./util";
import { send } from "@utils/fetch";
import { getSurveyAnswers } from "src/hooks/survey/answers";
import { captureException } from "src/utils/error";
import { KnownSurveyAnswersState } from "@utils/redux/slices/surveyAnswers";
import { isNoomPremiumConsultationSurveyDisqualified } from "src/utils/userSegment/features";

type Params = {
  finishedEvent?: string;

  dateKey?: string;
  extraProps?: JsonObject;
};

export const saveAnswers = async ({
  params = {},
  store,
  pageSet,
  page,
}: {
  params?: Params;
} & Omit<ActionHandlerParams, "params">) => {
  const { dateKey, extraProps } = params;

  const survey = getPageSetSurvey(pageSet, page);

  const { surveyNameSpace } = survey;
  const { currentAnswers } = store.getState();

  const internalNamespaceName = getInternalSurveyNamespace(surveyNameSpace);

  let surveyAnswers = currentAnswers.questionAnswers[surveyNameSpace];

  if (surveyNameSpace === "mainSurvey") {
    surveyAnswers = await store.dispatch(
      saveMainSurveyAnswers(surveyAnswers, {
        surveyNameSpace: internalNamespaceName,
        dateKey,
        extraProps,
      })
    );
  } else {
    surveyAnswers = await store.dispatch(
      saveSurveyAnswers(surveyAnswers, {
        surveyNameSpace: internalNamespaceName,
        overwrite: false,
        dateKey,
        extraProps,
      })
    );
  }

  store.dispatch(
    resetCurrentAnswers({
      namespace: surveyNameSpace,
      answers: {},
    })
  );

  const finishedEvent = params.finishedEvent || `Finished${survey.name}`;
  trackEvent(finishedEvent, surveyAnswers);

  return surveyAnswers;
};

/**
 * Sends the relevant responses from the premium post-purchase survey to the backend to configure
 * premium features for the given user.
 */
export const saveNoomPremiumAnswers = async ({
  store,
}: ActionHandlerParams) => {
  const state = store.getState();
  const surveyAnswers = getSurveyAnswers(state);
  const isDisqualifiedFromWorkoutPlan =
    isNoomPremiumConsultationSurveyDisqualified(state);

  // NOTE(alexl): Here the intention is to prevent errors in submitting the Noom Premium config data from derailing
  // the buyflow. Potential error causes would be renaming or removing the NP post-purchase survey responses which are
  // submitted to the backend. Such errors are captured and accounted for by the backend, so here it's acceptable to
  // ignore and proceed. Long-term the plan is to remove this submit-config logic from the buyflow and port it to the
  // app.
  try {
    await submitNoomPremiumAnswers(
      state.userData,
      surveyAnswers,
      isDisqualifiedFromWorkoutPlan
    );
  } catch (error) {
    captureException(error, "saveNoomPremiumAnswers");
  }
};

function submitNoomPremiumAnswers(
  userData: UserDataState,
  surveyAnswers: KnownSurveyAnswersState,
  isDisqualifiedFromWorkoutPlan: boolean
) {
  // NOTE(alexl): users with a physical limitation that disqualifies them from receiving a workout plan do not submit
  // responses for workout frequency/equipment preferences. However these fields are required by the backend API so we
  // provide dummy values here.
  const workoutFrequency = isDisqualifiedFromWorkoutPlan
    ? "DISQUALIFIED"
    : surveyAnswers.workoutPlanFrequency?.[0];
  const workoutEquipment = isDisqualifiedFromWorkoutPlan
    ? "DISQUALIFIED"
    : surveyAnswers.workoutPlanEquipment?.[0];

  const payload = {
    language: userData.language,
    country: userData.country,
    dietaryRestriction: surveyAnswers.mealPlanDietaryRestrictions?.[0],
    workoutLimitations: surveyAnswers.workoutPlanLimitations?.[0],
    workoutFrequencyPreference: workoutFrequency,
    workoutEquipmentPreference: workoutEquipment,
  };

  return send("POST", "/api/premium/v1/features/", payload);
}

function getInternalSurveyNamespace(
  pageSetSurveyNamespace: Survey["surveyNameSpace"]
) {
  // saveSurveyAnswers stores in userData slice. This check ensures that the JSON config
  // will properly load that data in the future/on refresh.
  invariant(
    pageSetSurveyNamespace.startsWith("userData.") ||
      pageSetSurveyNamespace === "mainSurvey",
    "surveyNameSpace must start with userData for this action."
  );

  let internalNamespaceName: keyof UserDataState | "mainSurvey" =
    pageSetSurveyNamespace.replace("userData.", "") as any;
  if (internalNamespaceName === "mainSurvey") {
    if (isHM()) {
      internalNamespaceName = "personaSurveyHM";
    } else if (!isUS()) {
      internalNamespaceName = "personaSurveyNonUS";
    } else {
      internalNamespaceName = "personaSurveyUS";
    }
  }
  return internalNamespaceName;
}
